import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Save, Eye, Upload, Trash2, Plus, GripVertical, Settings2, 
  Loader2, CheckCircle, AlertTriangle, ChevronDown, ChevronUp,
  Image as ImageIcon
} from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { apiClient } from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

// Lucide icon names for picker
const AVAILABLE_ICONS = [
  'Home', 'Building2', 'Users', 'TrendingUp', 'Award', 'Shield', 'CheckCircle',
  'Clock', 'MapPin', 'Phone', 'Mail', 'Target', 'Zap', 'Globe', 'Lock',
  'Key', 'DollarSign', 'Percent', 'Star', 'Heart', 'Briefcase', 'Lightbulb'
];

export default function PageBuilder() {
  const { pageKey } = useParams();
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [mode, setMode] = useState('draft'); // draft, published
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadLayout();
  }, [mode]);

  const loadLayout = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/api/page-layouts/${pageKey}?mode=${mode}`);
      
      if (response.sections && response.sections.length > 0) {
        setSections(response.sections);
      } else {
        // Initialize with default sections based on page
        const defaultSections = getDefaultSections(pageKey);
        setSections(defaultSections);
        setHasChanges(true);
      }
    } catch (error) {
      console.error('Failed to load layout:', error);
      toast.error('Грешка при зареждане на страницата');
    } finally {
      setLoading(false);
    }
  };

  const getDefaultSections = (page) => {
    // Default sections for different pages
    const defaults = {
      home: [
        { section_key: 'hero', order_index: 0, is_enabled: true, props_json: { title: 'Начална страница' }},
        { section_key: 'trust', order_index: 1, is_enabled: true, props_json: { badges: [] }},
        { section_key: 'services', order_index: 2, is_enabled: true, props_json: { title: 'Услуги' }}
      ],
      about: [
        { section_key: 'hero', order_index: 0, is_enabled: true, props_json: { title: 'За нас' }},
        { section_key: 'team', order_index: 1, is_enabled: true, props_json: { title: 'Нашият екип' }}
      ],
      services: [
        { section_key: 'hero', order_index: 0, is_enabled: true, props_json: { title: 'Услуги' }},
        { section_key: 'services', order_index: 1, is_enabled: true, props_json: { title: 'Какво предлагаме' }}
      ],
      contact: [
        { section_key: 'hero', order_index: 0, is_enabled: true, props_json: { title: 'Контакти' }},
        { section_key: 'cta', order_index: 1, is_enabled: true, props_json: { title: 'Свържете се с нас' }}
      ]
    };
    
    return defaults[page] || defaults.home;
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const reordered = Array.from(sections);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);

    // Update order_index
    const updated = reordered.map((section, index) => ({
      ...section,
      order_index: index
    }));

    setSections(updated);
    setHasChanges(true);
  };

  const handleSaveDraft = async () => {
    try {
      setSaving(true);
      
      const sectionsData = sections.map((section, index) => ({
        section_key: section.section_key,
        order_index: index,
        props_json: section.props_json || {},
        is_enabled: section.is_enabled !== false
      }));

      await apiClient.post(`/api/page-layouts/${pageKey}/save-draft`, sectionsData);
      
      toast.success('Черновата е запазена');
      setHasChanges(false);
    } catch (error) {
      console.error('Save failed:', error);
      toast.error('Грешка при запазване');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!window.confirm('Сигурни ли сте, че искате да публикувате промените? Те ще се видят на публичния сайт веднага.')) {
      return;
    }

    try {
      setSaving(true);
      
      // First save draft
      await handleSaveDraft();
      
      // Then publish
      await apiClient.post(`/api/page-layouts/${pageKey}/publish`, {});
      
      toast.success('Страницата е публикувана успешно!');
      setMode('published');
      loadLayout();
    } catch (error) {
      console.error('Publish failed:', error);
      toast.error('Грешка при публикуване');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleSection = (index) => {
    const updated = [...sections];
    updated[index] = {
      ...updated[index],
      is_enabled: !updated[index].is_enabled
    };
    setSections(updated);
    setHasChanges(true);
  };

  const handleUpdateSectionProps = (index, newProps) => {
    const updated = [...sections];
    updated[index] = {
      ...updated[index],
      props_json: {
        ...updated[index].props_json,
        ...newProps
      }
    };
    setSections(updated);
    setHasChanges(true);
  };

  const renderSectionPreview = (section) => {
    const props = section.props_json || {};
    
    switch (section.section_key) {
      case 'hero':
        return (
          <div className="bg-gradient-to-br from-primary-black to-primary-charcoal p-8 rounded-lg">
            <h1 className="text-3xl font-playfair font-bold text-accent-gold mb-3">
              {props.title || 'Hero Title'}
            </h1>
            <p className="text-neutral-300 mb-4">{props.subtitle || 'Hero Subtitle'}</p>
            <button className="px-6 py-2 bg-accent-gold text-primary-black rounded-lg">
              {props.cta_text || 'CTA Button'}
            </button>
          </div>
        );
      
      case 'trust':
        return (
          <div className="flex gap-4 p-6">
            {(props.badges || []).map((badge, i) => (
              <div key={i} className="flex-1 text-center p-4 bg-primary-charcoal rounded-lg">
                <div className="text-accent-gold mb-2">{badge.icon_name}</div>
                <div className="text-sm">{badge.text}</div>
              </div>
            ))}
          </div>
        );
      
      case 'services':
        return (
          <div className="p-6">
            <h2 className="text-2xl font-playfair font-bold mb-4">{props.title || 'Services'}</h2>
            <div className="grid grid-cols-3 gap-4">
              {(props.items || []).map((item, i) => (
                <div key={i} className="p-4 bg-primary-charcoal rounded-lg">
                  <div className="text-accent-gold mb-2">{item.icon_name}</div>
                  <h3 className="font-semibold mb-1">{item.title}</h3>
                  <p className="text-sm text-neutral-400">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        );
      
      default:
        return (
          <div className="p-6 bg-primary-charcoal rounded-lg text-center text-neutral-400">
            {section.section_key} section
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-accent-gold" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-black">
      {/* Top Bar */}
      <div className="sticky top-0 z-50 bg-primary-charcoal border-b border-neutral-700 px-6 py-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div>
            <h1 className="text-xl font-bold">Visual Page Builder - {pageKey}</h1>
            <p className="text-sm text-neutral-400">
              Режим: <span className="text-accent-gold">{mode === 'draft' ? 'Чернова' : 'Публикувано'}</span>
            </p>
          </div>

          <div className="flex items-center gap-3">
            {hasChanges && (
              <span className="text-sm text-yellow-400 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Незапазени промени
              </span>
            )}

            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              className="px-3 py-2 bg-primary-black border border-neutral-700 rounded-lg text-sm"
            >
              <option value="draft">Чернова</option>
              <option value="published">Публикувано</option>
            </select>

            <Button
              variant="outline"
              onClick={handleSaveDraft}
              disabled={saving || !hasChanges}
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Запази Чернова
            </Button>

            <Button
              onClick={handlePublish}
              disabled={saving}
              className="bg-accent-gold hover:bg-accent-gold-dark text-primary-black"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Публикувай
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Left Panel - Sections List */}
          <div className="col-span-3 space-y-4">
            <div className="bg-primary-charcoal rounded-lg border border-neutral-700 p-4">
              <h2 className="font-semibold mb-4">Секции</h2>
              
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="sections">
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="space-y-2"
                    >
                      {sections.map((section, index) => (
                        <Draggable
                          key={`${section.section_key}-${index}`}
                          draggableId={`${section.section_key}-${index}`}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`p-3 rounded-lg border transition-colors ${
                                snapshot.isDragging
                                  ? 'bg-accent-gold/20 border-accent-gold'
                                  : selectedSection === index
                                  ? 'bg-primary-black border-accent-gold'
                                  : 'bg-primary-black border-neutral-700 hover:border-neutral-600'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <div {...provided.dragHandleProps}>
                                  <GripVertical className="w-4 h-4 text-neutral-500" />
                                </div>
                                
                                <div
                                  className="flex-1 cursor-pointer"
                                  onClick={() => setSelectedSection(index)}
                                >
                                  <div className="font-medium capitalize text-sm">
                                    {section.section_key}
                                  </div>
                                </div>

                                <button
                                  onClick={() => handleToggleSection(index)}
                                  className={`p-1 rounded transition-colors ${
                                    section.is_enabled
                                      ? 'text-green-400 hover:bg-green-900/30'
                                      : 'text-neutral-600 hover:bg-neutral-800'
                                  }`}
                                  title={section.is_enabled ? 'Активна' : 'Неактивна'}
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          </div>

          {/* Center Panel - Preview */}
          <div className="col-span-6">
            <div className="bg-primary-charcoal rounded-lg border border-neutral-700 overflow-hidden">
              <div className="bg-primary-black px-4 py-3 border-b border-neutral-700">
                <h2 className="font-semibold">Преглед</h2>
              </div>
              
              <div className="p-4 space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
                {sections.map((section, index) => (
                  <div
                    key={index}
                    className={`relative ${!section.is_enabled ? 'opacity-40' : ''}`}
                    onClick={() => setSelectedSection(index)}
                  >
                    {!section.is_enabled && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10 rounded-lg">
                        <span className="text-sm font-medium bg-neutral-800 px-3 py-1 rounded">
                          Неактивна
                        </span>
                      </div>
                    )}
                    {renderSectionPreview(section)}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Panel - Properties */}
          <div className="col-span-3">
            {selectedSection !== null && sections[selectedSection] ? (
              <SectionPropertiesPanel
                section={sections[selectedSection]}
                onUpdate={(newProps) => handleUpdateSectionProps(selectedSection, newProps)}
              />
            ) : (
              <div className="bg-primary-charcoal rounded-lg border border-neutral-700 p-6 text-center text-neutral-400">
                <Settings2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">Изберете секция за редакция</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Section Properties Panel Component
function SectionPropertiesPanel({ section, onUpdate }) {
  const props = section.props_json || {};

  const handleChange = (key, value) => {
    onUpdate({ [key]: value });
  };

  return (
    <div className="bg-primary-charcoal rounded-lg border border-neutral-700 p-4 space-y-4">
      <h2 className="font-semibold flex items-center gap-2">
        <Settings2 className="w-4 h-4" />
        Настройки: {section.section_key}
      </h2>

      {section.section_key === 'hero' && (
        <>
          <div>
            <label className="block text-sm font-medium mb-2">Заглавие</label>
            <Input
              value={props.title || ''}
              onChange={(e) => handleChange('title', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Подзаглавие</label>
            <textarea
              value={props.subtitle || ''}
              onChange={(e) => handleChange('subtitle', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-primary-black border border-neutral-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">CTA Текст</label>
            <Input
              value={props.cta_text || ''}
              onChange={(e) => handleChange('cta_text', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">CTA Link</label>
            <Input
              value={props.cta_link || ''}
              onChange={(e) => handleChange('cta_link', e.target.value)}
            />
          </div>
        </>
      )}

      {section.section_key === 'services' && (
        <div>
          <label className="block text-sm font-medium mb-2">Заглавие</label>
          <Input
            value={props.title || ''}
            onChange={(e) => handleChange('title', e.target.value)}
          />
          <p className="text-xs text-neutral-500 mt-2">
            За редакция на услугите, използвайте Content Manager
          </p>
        </div>
      )}
    </div>
  );
}
